import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { fetchAPI } from '../../ducks/api';
import { loadUser } from '../../ducks/Auth/actions';
import { hasMinimumRole } from '../../utils/permissions';
import { resolveViewMode } from '../../utils/viewMode';
import './index.css';

/**
 * TrainerTour — a one-minute spotlight walkthrough of the Trainer studio.
 *
 * Opens by itself the first time a coach reaches the studio (until they finish
 * or skip it — saved on their account as `tourCompletedAt`, so it's once per
 * coach, not per browser), and again from "Take the tour" in the account menu
 * (startTrainerTour()).
 *
 * Each step highlights a sidebar element tagged `data-tour="<key>"`. Steps
 * adapt to the coach: Trainers / Challenges only when their role has them, the
 * checklist only while it's showing, and the copy changes when they have no
 * clients yet or haven't set up their brand. On phones it opens the menu
 * drawer for the sidebar steps; a missing target just shows the card centered.
 */

export const START_TOUR_EVENT = 'nubod:start-tour';
export const TOUR_CLOSED_EVENT = 'nubod:tour-closed';

// True from the moment a tour starts (before its first card renders) until
// it closes — so other first-visit popups (the notifications prompt) can wait
// their turn instead of stacking on top of it.
let tourActive = false;
export const isTourActive = () => tourActive;
export const startTrainerTour = () => {
  try {
    window.dispatchEvent(new CustomEvent(START_TOUR_EVENT));
  } catch (_) {
    /* very old browsers: no tour */
  }
};

// Closed this session (finished or skipped) — don't reopen before the account
// flag round-trips, even if saving it failed.
const SESSION_KEY = 'nubod.tourClosed';
const readSessionClosed = () => {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch (_) {
    return false;
  }
};
const writeSessionClosed = () => {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch (_) {
    /* private mode */
  }
};

const PAD = 6; // spotlight breathing room around the target
const GAP = 14; // card ↔ target
const EDGE = 16; // card ↔ viewport edge

function buildSteps({ user, status }) {
  const role = user.role || 'trainer';
  const firstName = (user.name || '').trim().split(/\s+/)[0] || '';
  const hasClients = !!(status && status.hasClients);
  const hasBrand = !!(status && status.hasBusinessIdentity);
  const steps = [
    {
      key: 'intro',
      title: firstName ? `Welcome to your studio, ${firstName}` : 'Welcome to your studio',
      body:
        'A quick tour of where your clients live, where you build their training and nutrition, and how it reaches them. About a minute; skip anytime.',
      primary: 'Show me around',
      secondary: 'Skip tour',
    },
    {
      key: 'create',
      target: 'create',
      title: 'Create',
      body: 'Add a client, workout, program, meal plan or exercise from any page.',
    },
    {
      key: 'home',
      target: 'home',
      title: 'Home',
      body:
        'Your day at a glance: what needs your attention, who’s at risk of slipping, and what your clients logged.',
    },
    {
      key: 'clients',
      target: 'clients',
      title: 'Clients',
      body: hasClients
        ? 'Everyone you coach. Open a client to see their program, workouts, nutrition, check-ins, measurements and photos, and to change their plan.'
        : 'Invite your first client here. They get an email to set up their account and get the app. Tip: invite yourself with a second email to see what clients see.',
    },
  ];
  if (hasMinimumRole(role, 'organization_admin')) {
    steps.push({
      key: 'trainers',
      target: 'trainers',
      title: 'Trainers',
      body: 'Add the trainers on your team and see who coaches whom.',
    });
  }
  steps.push({
    key: 'library',
    target: 'library',
    title: 'Library',
    body:
      'Build once, use with every client: workouts, programs (weeks of workouts), plans (programs in phases), your own exercises with video, meal plans and assessments.',
  });
  if (hasMinimumRole(role, 'platform_admin')) {
    steps.push({
      key: 'challenges',
      target: 'challenges',
      title: 'Challenges',
      body:
        'Run a group challenge on one of your plans: everyone starts together, scores on a leaderboard, and can sign up from a public page.',
    });
  }
  steps.push(
    {
      key: 'messages',
      target: 'messages',
      title: 'Messages',
      body: 'Chat with each client one-on-one, or send a broadcast to many at once.',
    },
    {
      key: 'get-started',
      target: 'get-started',
      optional: true, // only while the checklist is on screen
      title: 'Your setup checklist',
      body: 'The next few things to set up. It ticks itself off as you go.',
    },
    {
      key: 'account',
      target: 'account',
      title: 'Your account',
      body: hasBrand
        ? 'Account settings: your business details, brand and notifications. You can replay this tour from here too.'
        : 'Open Account settings → Business to add your business name, logo and colors. Your clients see your brand, not ours. You can replay this tour from here too.',
    },
    {
      key: 'finish',
      title: 'Your clients use the app',
      body:
        'Clients log workouts, food and check-ins in the Nubod iPhone app, and you see it all here.',
      primary: hasClients ? 'Done' : 'Invite your first client',
      primaryHref: hasClients ? null : '/dashboard/admin/clients?invite=1',
      secondary: hasClients ? null : 'Done',
    }
  );
  return steps;
}

const targetEl = (key) =>
  key ? document.querySelector(`[data-tour="${key}"]`) : null;

// A target counts only if it's actually laid out (a hidden checklist, a
// closed accordion, etc. measure 0×0).
function visibleRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom };
}

function placeCard(rect, card, vw, vh) {
  const w = card.width;
  const h = card.height;
  if (!rect) {
    return { top: Math.max(EDGE, (vh - h) / 2), left: Math.max(EDGE, (vw - w) / 2), placement: 'center' };
  }
  const clampTop = (t) => Math.min(Math.max(EDGE, t), Math.max(EDGE, vh - h - EDGE));
  const clampLeft = (l) => Math.min(Math.max(EDGE, l), Math.max(EDGE, vw - w - EDGE));
  // Sidebar targets sit on the left: prefer the card to their right.
  if (vw - rect.right - PAD >= w + GAP + EDGE) {
    return {
      top: clampTop(rect.top + rect.height / 2 - h / 2),
      left: rect.right + PAD + GAP,
      placement: 'right',
      arrowTop: Math.min(Math.max(18, rect.top + rect.height / 2 - clampTop(rect.top + rect.height / 2 - h / 2)), h - 18),
    };
  }
  if (vh - rect.bottom - PAD >= h + GAP + EDGE) {
    return { top: rect.bottom + PAD + GAP, left: clampLeft(rect.left), placement: 'below' };
  }
  if (rect.top - PAD >= h + GAP + EDGE) {
    return { top: rect.top - PAD - GAP - h, left: clampLeft(rect.left), placement: 'above' };
  }
  return { top: Math.max(EDGE, vh - h - EDGE), left: clampLeft((vw - w) / 2), placement: 'bottom' };
}

function TrainerTour({ auth, location, history, isMobile, setMobileNavOpen, loadUser }) {
  const user = auth && auth.user;
  const path = (location && location.pathname) || '';
  const eligible =
    !!(auth && auth.isAuthenticated && user && user._id) &&
    hasMinimumRole(user.role || 'client', 'trainer') &&
    resolveViewMode(user, auth.viewMode) !== 'client';

  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [pos, setPos] = useState(null);
  const cardRef = useRef(null);
  const primaryRef = useRef(null);
  const startedRef = useRef(false);

  const start = useCallback(async () => {
    tourActive = true;
    let status = null;
    try {
      status = await fetchAPI('/trainer/onboarding-status', { method: 'GET' }, undefined);
    } catch (_) {
      /* tailor copy when we can; the tour works without it */
    }
    const all = buildSteps({ user: user || {}, status });
    // Drop steps whose target isn't in the page at all (e.g. a role without
    // that section). Optional ones also need to be on screen right now.
    const usable = all.filter((s) => {
      if (!s.target) return true;
      const el = targetEl(s.target);
      if (!el) return false;
      return s.optional ? !!visibleRect(el) : true;
    });
    setSteps(usable);
    setIndex(0);
    setOpen(true);
  }, [user]);

  // First visit: open once the studio has rendered.
  useEffect(() => {
    if (!eligible || open || startedRef.current) return undefined;
    if (user.tourCompletedAt || readSessionClosed()) return undefined;
    if (!path.startsWith('/dashboard/admin')) return undefined;
    const t = setTimeout(() => {
      startedRef.current = true;
      start();
    }, 900);
    return () => clearTimeout(t);
  }, [eligible, open, user, path, start]);

  // "Take the tour" from the account menu.
  useEffect(() => {
    const onStart = () => {
      if (!eligible) return;
      startedRef.current = true;
      start();
    };
    window.addEventListener(START_TOUR_EVENT, onStart);
    return () => window.removeEventListener(START_TOUR_EVENT, onStart);
  }, [eligible, start]);

  const step = open ? steps[index] : null;

  // Phones: the sidebar is a drawer — open it while a sidebar step is showing.
  useEffect(() => {
    if (!open || !isMobile || !setMobileNavOpen) return;
    setMobileNavOpen(!!(step && step.target));
  }, [open, isMobile, step, setMobileNavOpen]);

  // Measure the target (again after the drawer slides in, on resize/scroll).
  useEffect(() => {
    if (!open || !step) return undefined;
    let frame = 0;
    let tries = 0;
    const measure = () => {
      const el = targetEl(step.target);
      if (el && tries === 0 && el.scrollIntoView) {
        try {
          el.scrollIntoView({ block: 'nearest' });
        } catch (_) {
          /* ignore */
        }
      }
      setRect(visibleRect(el));
      // Keep measuring briefly so a sliding drawer / opening accordion settles.
      if (tries++ < 20) frame = requestAnimationFrame(measure);
    };
    measure();
    const onChange = () => setRect(visibleRect(targetEl(step.target)));
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [open, step]);

  // Place the card once it's rendered (it needs its own height).
  useLayoutEffect(() => {
    if (!open || !cardRef.current) return;
    const card = cardRef.current.getBoundingClientRect();
    setPos(placeCard(rect, card, window.innerWidth, window.innerHeight));
  }, [open, rect, index, steps]);

  // Focus the main button on every step (keyboard + screen readers).
  useEffect(() => {
    if (open && primaryRef.current) primaryRef.current.focus({ preventScroll: true });
  }, [open, index]);

  const finish = useCallback(
    async (href) => {
      setOpen(false);
      setRect(null);
      setPos(null);
      writeSessionClosed();
      tourActive = false;
      try {
        window.dispatchEvent(new CustomEvent(TOUR_CLOSED_EVENT));
      } catch (_) {
        /* ignore */
      }
      if (isMobile && setMobileNavOpen) setMobileNavOpen(false);
      if (href && history) history.push(href);
      try {
        await fetchAPI('/users/me/tour', { method: 'PATCH' }, { done: true });
        if (loadUser) loadUser();
      } catch (_) {
        /* the session flag keeps it closed until the next visit */
      }
    },
    [history, isMobile, setMobileNavOpen, loadUser]
  );

  const next = useCallback(() => {
    if (index >= steps.length - 1) {
      finish(step && step.primaryHref);
      return;
    }
    setIndex((i) => i + 1);
  }, [index, steps.length, finish, step]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // Keyboard: Esc skips, ←/→ move, Tab stays inside the card.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      } else if (e.key === 'Tab' && cardRef.current) {
        const items = cardRef.current.querySelectorAll('button');
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, next, back, finish]);

  if (!open || !step) return null;

  const total = steps.length;
  const isIntro = index === 0;
  const isLast = index === total - 1;
  const spot = rect
    ? {
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  return (
    <div className="tt-root" aria-live="polite">
      {/* Blocks the page while the tour runs; the spotlight is the only "hole". */}
      <div className={`tt-catch ${spot ? '' : 'tt-dim'}`} aria-hidden="true" />
      {spot ? <div className="tt-spot" style={spot} aria-hidden="true" /> : null}
      <div
        ref={cardRef}
        className={`tt-card tt-${(pos && pos.placement) || 'center'}`}
        style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tt-title"
        aria-describedby="tt-body"
      >
        {pos && pos.placement === 'right' ? (
          <span className="tt-arrow" style={{ top: pos.arrowTop }} aria-hidden="true" />
        ) : null}
        <div className="tt-head">
          {!isIntro ? (
            <span className="tt-count">
              {index} of {total - 1}
            </span>
          ) : (
            <span className="tt-count">Studio tour</span>
          )}
          {!isIntro && !isLast ? (
            <button type="button" className="tt-skip" onClick={() => finish()}>
              Skip tour
            </button>
          ) : null}
        </div>
        <h2 className="tt-title" id="tt-title">
          {step.title}
        </h2>
        <p className="tt-body" id="tt-body">
          {step.body}
        </p>
        {!isIntro ? (
          <div className="tt-dots" aria-hidden="true">
            {steps.slice(1).map((s, i) => (
              <i key={s.key} className={i + 1 === index ? 'on' : i + 1 < index ? 'done' : ''} />
            ))}
          </div>
        ) : null}
        <div className="tt-actions">
          {isIntro || isLast ? (
            step.secondary ? (
              <button type="button" className="tt-btn tt-ghost" onClick={() => finish()}>
                {step.secondary}
              </button>
            ) : (
              <span />
            )
          ) : (
            <button type="button" className="tt-btn tt-ghost" onClick={back}>
              Back
            </button>
          )}
          <button type="button" className="tt-btn tt-primary" ref={primaryRef} onClick={next}>
            {step.primary || 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({ auth: state.auth });

export default withRouter(connect(mapStateToProps, { loadUser })(TrainerTour));
